/** World axis a section plane is perpendicular to. */
export type HsSectionAxis = 'x' | 'y' | 'z';

/** What the section cuts: nothing, everything on one side of a plane, or everything outside a box. */
export type HsSectionMode = 'off' | 'plane' | 'box';

/** An axis-aligned box in world coordinates. */
export interface HsSectionBounds {
  min: readonly [number, number, number];
  max: readonly [number, number, number];
}

/** A clipping plane `normal · p + constant = 0` cutting away the side where it is positive, as scene looks take it. */
export interface HsSectionPlane {
  normal: readonly [number, number, number];
  constant: number;
}

const AXIS_INDEX: Readonly<Record<HsSectionAxis, 0 | 1 | 2>> = { x: 0, y: 1, z: 2 };
/** The thinnest a section box gets when its faces are moved, in meters. */
const MIN_BOX_SIZE: number = 0.01;
/** A face normal within a degree of a world axis cuts across that axis. */
const AXIS_SNAP_COSINE: number = Math.cos(Math.PI / 180);

/**
 * Section state of a viewer as CAD and BIM viewers offer it: a plane across one world axis (cutting away the far
 * side, or the near side when flipped) or a box that keeps only what is inside. Positions are world coordinates,
 * so the section works for models at survey coordinates; the host turns `planes` into its scene look.
 */
export class HsSectionTool {
  public mode: HsSectionMode = 'off';
  public axis: HsSectionAxis = 'x';
  /** Cut away the low side of the plane instead of the high side. */
  public flipped: boolean = false;
  /**
   * The normal of a plane cut through a face (`cutThroughFace`), or null for a plane across `axis`. Positions then
   * run along this normal.
   */
  public customNormal: readonly [number, number, number] | null = null;
  /** Where the plane crosses its axis (or the face normal), in world meters along it. */
  public position: number = 0;
  /** The kept box in box mode. */
  public box: HsSectionBounds | null = null;
  /** The extent of what is sectioned, which bounds the plane position; null before a scene is known. */
  private boundsValue: HsSectionBounds | null = null;

  public get bounds(): HsSectionBounds | null {
    return this.boundsValue;
  }

  /** The unit direction the plane position runs along: the axis, or the face normal of a face cut. */
  public get direction(): [number, number, number] {
    if (this.customNormal !== null) {
      return [this.customNormal[0], this.customNormal[1], this.customNormal[2]];
    }
    const direction: [number, number, number] = [0, 0, 0];
    direction[AXIS_INDEX[this.axis]] = 1;
    return direction;
  }

  /** The range the plane position can take along its direction, or null without bounds. */
  public get range(): { min: number; max: number } | null {
    const bounds: HsSectionBounds | null = this.boundsValue;
    if (bounds === null) {
      return null;
    }
    const along: number[] = boxCorners(bounds).map((corner: number[]) => dot(corner, this.direction));
    return { min: Math.min(...along), max: Math.max(...along) };
  }

  /** Sets the sectioned extent; a plane outside it moves to its middle. */
  public setBounds(bounds: HsSectionBounds | null): void {
    this.boundsValue = bounds === null ? null : normalizeBounds(bounds);
    this.clampPosition(true);
  }

  public setAxis(axis: HsSectionAxis): void {
    this.axis = axis;
    this.customNormal = null;
    this.clampPosition(true);
  }

  public setPosition(position: number): void {
    this.position = position;
    this.clampPosition(false);
  }

  /** Moves the plane to the middle of the bounds on its axis, as when a section is turned on. */
  public center(): void {
    const range: { min: number; max: number } | null = this.range;
    if (range !== null) {
      this.position = (range.min + range.max) / 2;
    }
  }

  /** Cuts through a world point, such as one picked on the model, across the current direction. */
  public cutAt(point: readonly [number, number, number]): void {
    this.mode = 'plane';
    this.setPosition(dot([point[0], point[1], point[2]], this.direction));
  }

  /**
   * Cuts along a face: through `point`, perpendicular to the face `normal`, cutting away the side the normal points
   * to (in front of the face). A normal within a degree of a world axis becomes that axis.
   */
  public cutThroughFace(point: readonly [number, number, number], normal: readonly [number, number, number]): void {
    const size: number = Math.hypot(normal[0], normal[1], normal[2]);
    if (size === 0) {
      this.cutAt(point);
      return;
    }
    const unit: [number, number, number] = [normal[0] / size, normal[1] / size, normal[2] / size];
    const axisIndex: number = unit.findIndex((value: number) => Math.abs(value) > AXIS_SNAP_COSINE);
    if (axisIndex >= 0) {
      this.axis = (['x', 'y', 'z'] as const)[axisIndex];
      this.customNormal = null;
      this.flipped = unit[axisIndex] < 0;
    } else {
      this.customNormal = unit;
      this.flipped = false;
    }
    this.cutAt(point);
  }

  /** Moves the plane along its direction, as when dragging it. */
  public moveBy(distance: number): void {
    this.setPosition(this.position + distance);
  }

  /** Keeps only what lies inside `box`, grown by `margin` meters so its own faces stay visible. */
  public boxAround(box: HsSectionBounds, margin: number = 0.05): void {
    const normalized: HsSectionBounds = normalizeBounds(box);
    this.mode = 'box';
    this.box = {
      min: [normalized.min[0] - margin, normalized.min[1] - margin, normalized.min[2] - margin],
      max: [normalized.max[0] + margin, normalized.max[1] + margin, normalized.max[2] + margin],
    };
  }

  /**
   * The world box of what the section can show, for limiting downloads to it (a Frustum `cutArea`): the box in
   * box mode; in plane mode across an axis, the bounds cut at the plane. Null when everything may show (off, a
   * face plane, or no bounds).
   */
  public cutBox(): HsSectionBounds | null {
    if (this.mode === 'box') {
      return this.box;
    }
    const bounds: HsSectionBounds | null = this.boundsValue;
    if (this.mode !== 'plane' || this.customNormal !== null || bounds === null) {
      return null;
    }
    const index: number = AXIS_INDEX[this.axis];
    const min: [number, number, number] = [bounds.min[0], bounds.min[1], bounds.min[2]];
    const max: [number, number, number] = [bounds.max[0], bounds.max[1], bounds.max[2]];
    // The far (high) side is cut away, or the near side when flipped.
    if (this.flipped) {
      min[index] = Math.max(min[index], this.position);
    } else {
      max[index] = Math.min(max[index], this.position);
    }
    return { min, max };
  }

  /**
   * Moves one face of the box: the `min` or `max` side on an axis, kept at least `MIN_BOX_SIZE` from the
   * opposite face.
   */
  public setBoxFace(axis: HsSectionAxis, side: 'min' | 'max', value: number): void {
    if (this.box === null || !Number.isFinite(value)) {
      return;
    }
    const index: number = AXIS_INDEX[axis];
    const min: [number, number, number] = [this.box.min[0], this.box.min[1], this.box.min[2]];
    const max: [number, number, number] = [this.box.max[0], this.box.max[1], this.box.max[2]];
    if (side === 'min') {
      min[index] = Math.min(value, max[index] - MIN_BOX_SIZE);
    } else {
      max[index] = Math.max(value, min[index] + MIN_BOX_SIZE);
    }
    this.box = { min, max };
  }

  /**
   * The box face a camera ray enters through (slab test), to grab it for resizing; null when the ray misses the
   * box or there is none. A ray starting inside the box takes the face it leaves through.
   */
  public pickBoxFace(
    origin: readonly [number, number, number],
    direction: readonly [number, number, number]
  ): { axis: HsSectionAxis; side: 'min' | 'max' } | null {
    if (this.box === null) {
      return null;
    }
    let near: number = -Infinity;
    let far: number = Infinity;
    let nearFace: { axis: HsSectionAxis; side: 'min' | 'max' } | null = null;
    let farFace: { axis: HsSectionAxis; side: 'min' | 'max' } | null = null;
    for (const axis of ['x', 'y', 'z'] as const) {
      const index: number = AXIS_INDEX[axis];
      if (Math.abs(direction[index]) < 1e-12) {
        if (origin[index] < this.box.min[index] || origin[index] > this.box.max[index]) {
          return null;
        }
        continue;
      }
      const toMin: number = (this.box.min[index] - origin[index]) / direction[index];
      const toMax: number = (this.box.max[index] - origin[index]) / direction[index];
      const [enter, leave]: [number, number] = toMin < toMax ? [toMin, toMax] : [toMax, toMin];
      const enterSide: 'min' | 'max' = toMin < toMax ? 'min' : 'max';
      if (enter > near) {
        near = enter;
        nearFace = { axis, side: enterSide };
      }
      if (leave < far) {
        far = leave;
        farFace = { axis, side: enterSide === 'min' ? 'max' : 'min' };
      }
    }
    if (near > far || far < 0) {
      return null;
    }
    return near >= 0 ? nearFace : farFace;
  }

  /** The center of a box face, for anchoring a drag of it. */
  public boxFaceCenter(axis: HsSectionAxis, side: 'min' | 'max'): [number, number, number] | null {
    if (this.box === null) {
      return null;
    }
    const center: [number, number, number] = [
      (this.box.min[0] + this.box.max[0]) / 2,
      (this.box.min[1] + this.box.max[1]) / 2,
      (this.box.min[2] + this.box.max[2]) / 2,
    ];
    const index: number = AXIS_INDEX[axis];
    center[index] = side === 'min' ? this.box.min[index] : this.box.max[index];
    return center;
  }

  /** The clipping planes of the section: none when off, one in plane mode, six in box mode. */
  public planes(): HsSectionPlane[] {
    if (this.mode === 'plane') {
      const sign: number = this.flipped ? -1 : 1;
      const direction: [number, number, number] = this.direction;
      const normal: [number, number, number] = [sign * direction[0], sign * direction[1], sign * direction[2]];
      return [{ normal, constant: -sign * this.position }];
    }
    if (this.mode === 'box' && this.box !== null) {
      const { min, max } = this.box;
      return [0, 1, 2].flatMap((index: number) => {
        const high: [number, number, number] = [0, 0, 0];
        const low: [number, number, number] = [0, 0, 0];
        high[index] = 1;
        low[index] = -1;
        return [
          { normal: high, constant: -max[index] },
          { normal: low, constant: min[index] },
        ];
      });
    }
    return [];
  }

  /** Whether a world point is cut away by the section. */
  public cuts(point: readonly [number, number, number]): boolean {
    return this.planes().some(
      (plane: HsSectionPlane) =>
        plane.normal[0] * point[0] + plane.normal[1] * point[1] + plane.normal[2] * point[2] + plane.constant > 0
    );
  }

  /**
   * Line segments (pairs of points, flat xyz) that outline the section: the plane's rectangle across the bounds,
   * or the box's twelve edges. Empty when off or without bounds for a plane.
   */
  public outlineVertices(): number[] {
    if (this.mode === 'box' && this.box !== null) {
      return boxEdges(this.box);
    }
    const bounds: HsSectionBounds | null = this.boundsValue;
    if (this.mode !== 'plane' || bounds === null) {
      return [];
    }
    const direction: number[] = this.direction;
    const [first, second] = planeBasis(direction, this.customNormal === null ? AXIS_INDEX[this.axis] : null);
    const along = (axis: number[]): number[] => boxCorners(bounds).map((corner: number[]) => dot(corner, axis));
    const firstRange: number[] = along(first);
    const secondRange: number[] = along(second);
    const corner = (a: number, b: number): number[] =>
      [0, 1, 2].map((index: number) => direction[index] * this.position + first[index] * a + second[index] * b);
    const [firstMin, firstMax] = [Math.min(...firstRange), Math.max(...firstRange)];
    const [secondMin, secondMax] = [Math.min(...secondRange), Math.max(...secondRange)];
    const corners: number[][] = [
      corner(firstMin, secondMin),
      corner(firstMax, secondMin),
      corner(firstMax, secondMax),
      corner(firstMin, secondMax),
    ];
    return corners.flatMap((point: number[], at: number) => [...point, ...corners[(at + 1) % 4]]);
  }

  private clampPosition(centerOutside: boolean): void {
    const range: { min: number; max: number } | null = this.range;
    if (range === null) {
      return;
    }
    if (centerOutside && (this.position < range.min || this.position > range.max)) {
      this.position = (range.min + range.max) / 2;
      return;
    }
    this.position = Math.min(range.max, Math.max(range.min, this.position));
  }
}

function normalizeBounds(bounds: HsSectionBounds): HsSectionBounds {
  return {
    min: [
      Math.min(bounds.min[0], bounds.max[0]),
      Math.min(bounds.min[1], bounds.max[1]),
      Math.min(bounds.min[2], bounds.max[2]),
    ],
    max: [
      Math.max(bounds.min[0], bounds.max[0]),
      Math.max(bounds.min[1], bounds.max[1]),
      Math.max(bounds.min[2], bounds.max[2]),
    ],
  };
}

function boxEdges(box: HsSectionBounds): number[] {
  const { min, max } = box;
  const corner = (x: boolean, y: boolean, z: boolean): number[] => [
    x ? max[0] : min[0],
    y ? max[1] : min[1],
    z ? max[2] : min[2],
  ];
  const edges: number[] = [];
  for (const a of [false, true]) {
    for (const b of [false, true]) {
      edges.push(...corner(false, a, b), ...corner(true, a, b));
      edges.push(...corner(a, false, b), ...corner(a, true, b));
      edges.push(...corner(a, b, false), ...corner(a, b, true));
    }
  }
  return edges;
}

function boxCorners(bounds: HsSectionBounds): number[][] {
  const corners: number[][] = [];
  for (const x of [bounds.min[0], bounds.max[0]]) {
    for (const y of [bounds.min[1], bounds.max[1]]) {
      for (const z of [bounds.min[2], bounds.max[2]]) {
        corners.push([x, y, z]);
      }
    }
  }
  return corners;
}

/** Two unit axes across a plane: the other world axes for an axis plane, else any perpendicular pair. */
function planeBasis(direction: number[], axisIndex: number | null): [number[], number[]] {
  if (axisIndex !== null) {
    const [first, second] = [0, 1, 2].filter((index: number) => index !== axisIndex);
    const unit = (index: number): number[] => [0, 1, 2].map((other: number) => (other === index ? 1 : 0));
    return [unit(first), unit(second)];
  }
  const helper: number[] = Math.abs(direction[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  const first: number[] = normalize(cross(helper, direction));
  return [first, normalize(cross(direction, first))];
}

function dot(a: readonly number[], b: readonly number[]): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function cross(a: readonly number[], b: readonly number[]): number[] {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function normalize(a: readonly number[]): number[] {
  const size: number = Math.hypot(a[0], a[1], a[2]);
  return size === 0 ? [0, 0, 0] : [a[0] / size, a[1] / size, a[2] / size];
}
